import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, PageHeader, Spinner } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, userService } from "../../services/userApi";
import socketService from "../../services/SocketService";
import UserChatConversation from "./UserChatConversation";
import SupportConversation, { SUPPORT_CHAT_ID, SupportChatItem } from "../lawyer/SupportConversation";

const listTime = (value) => {
  const m = moment(value);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return m.format("hh:mm A");
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("DD MMM");
};

const previewText = (msg) => {
  if (!msg) return "";
  if (msg.invoice_data) return "Invoice";
  if (msg.call_data) return "Video call";
  const label = { image: "Photo", video: "Video", audio: "Voice message", doc: "Document" }[msg.file_type];
  if (label) return label;
  return msg.message || "";
};

const UserChats = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { refreshProfile } = useOutletContext();
  const [chats, setChats] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const isSupport = chatId === SUPPORT_CHAT_ID;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  const load = useCallback(async (nextPage) => {
    try {
      const res = await userService.getChats(nextPage);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      const data = res.data.data || {};
      // The support chat comes back in this list for the apps; here it has its own pinned row.
      const list = (data.chats || []).filter((c) => !c.is_support);
      setChats((prev) => (nextPage === 1 ? list : [...prev, ...list]));
      setPage(data.pagination?.current_page || nextPage);
      setLastPage(data.pagination?.last_page || 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to load chats."));
    }
  }, []);

  useEffect(() => {
    load(1).finally(() => setLoading(false));
  }, [load]);

  // Moves a chat to the top with its newest message; unknown chats trigger a reload.
  const bumpChat = useCallback(
    (id, message, { unread = false } = {}) => {
      if (!chatsRef.current.some((c) => String(c.id) === String(id))) {
        load(1);
        return;
      }
      setChats((prev) => {
        const idx = prev.findIndex((c) => String(c.id) === String(id));
        if (idx === -1) return prev;
        const chat = {
          ...prev[idx],
          last_message: message,
          unread_count: unread ? (prev[idx].unread_count || 0) + 1 : prev[idx].unread_count,
        };
        return [chat, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
      });
    },
    [load]
  );

  useEffect(
    () =>
      socketService.subscribe("receive-message", (message) => {
        if (!message?.chat_id) return;
        const isOpen = String(message.chat_id) === String(chatId);
        bumpChat(message.chat_id, message, { unread: !isOpen });
      }),
    [bumpChat, chatId]
  );

  // Opening a conversation marks it read on the server; mirror that locally.
  useEffect(() => {
    if (!chatId) return;
    setChats((prev) => prev.map((c) => (String(c.id) === String(chatId) ? { ...c, unread_count: 0 } : c)));
  }, [chatId]);

  const loadMore = async () => {
    setLoadingMore(true);
    await load(page + 1);
    setLoadingMore(false);
  };

  return (
    <div className={`lwp-chat-layout${chatId ? " has-open" : ""}`}>
      <Seo title="Chats | Legal Platform" noindex />
      <section className="lwp-chat-list-pane">
        <div className="lwp-chat-list-head">
          <PageHeader title="Chats" backTo="/user/dashboard" />
        </div>
        <ul className="lwp-chat-list lwp-chat-list-pinned">
          <SupportChatItem api={userService} side="user" basePath="/user/chat" active={isSupport} />
        </ul>
        {loading ? (
          <Spinner />
        ) : chats.length === 0 ? (
          <EmptyState icon="bi-chat-dots" text="No conversations yet." />
        ) : (
          <ul className="lwp-chat-list">
            {chats.map((chat) => {
              const active = String(chat.id) === String(chatId);
              return (
                <li key={chat.id}>
                  <Link to={`/user/chat/${chat.id}`} className={`lwp-chat-item${active ? " active" : ""}`}>
                    <Avatar src={chat.lawyer?.picture} alt={chat.lawyer?.name} size={60} />
                    <span className="lwp-chat-item-body">
                      <span className="lwp-chat-item-row">
                        <strong>{chat.lawyer?.name || "Lawyer"}</strong>
                        <time>{listTime(chat.last_message?.created_at)}</time>
                      </span>
                      <span className="lwp-chat-item-row">
                        <span className="lwp-chat-item-preview">{previewText(chat.last_message)}</span>
                        {chat.unread_count > 0 && <span className="lwp-unread">{chat.unread_count}</span>}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
            {page < lastPage && (
              <li>
                <button type="button" className="lwp-load-more" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? <span className="spinner-border spinner-border-sm" /> : "Load more"}
                </button>
              </li>
            )}
          </ul>
        )}
      </section>

      <section className="lwp-chat-pane">
        {isSupport ? (
          <SupportConversation api={userService} side="user" onBack={() => navigate("/user/chat")} onActivity={() => refreshProfile()} />
        ) : chatId ? (
          <UserChatConversation
            key={chatId}
            chatId={chatId}
            onBack={() => navigate("/user/chat")}
            onActivity={(message) => {
              if (message) bumpChat(chatId, message);
              refreshProfile();
            }}
          />
        ) : (
          <div className="lwp-chat-empty">
            <i className="bi bi-chat-square-text" />
            <p>Select a conversation to start messaging</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default UserChats;
